import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import axios from "axios";
import { AXIOS_CONFIG } from "../api";
import type { AdditionalQuestionsConfig } from "@/types";
import { backend_url } from "@/constants.ts";
import { Endpoints } from "../api/endpoints";

export const useAdditionalQuestionsConfig = () => {
  const queryClient = useQueryClient();

  const fetchAdditionalQuestions =
    async (): Promise<AdditionalQuestionsConfig> => {
      const { data } = await axios.get<AdditionalQuestionsConfig>(
        `${backend_url}${Endpoints.additionalQuestions}`,
        {
          ...AXIOS_CONFIG,
        }
      );
      return data;
    };

  const updateAdditionalQuestions = async (
    updatedConfig: AdditionalQuestionsConfig
  ): Promise<AdditionalQuestionsConfig> => {
    const { data } = await axios.post<AdditionalQuestionsConfig>(
      `${backend_url}${Endpoints.additionalQuestions}`,
      updatedConfig,
      {
        ...AXIOS_CONFIG,
      }
    );
    return data;
  };

  const query = useQuery<AdditionalQuestionsConfig, AxiosError>({
    queryKey: ["additionalQuestionsConfig"],
    queryFn: fetchAdditionalQuestions,
    retry: 3,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const mutation = useMutation<
    AdditionalQuestionsConfig,
    AxiosError,
    AdditionalQuestionsConfig
  >({
    mutationFn: updateAdditionalQuestions,
    onSuccess: (data) => {
      queryClient.setQueryData(["additionalQuestionsConfig"], data);
    },
    onError: (error) => {
      console.error("Error updating additional questions:", error);
    },
  });

  const updateConfig = async (
    updatedConfig: AdditionalQuestionsConfig
  ): Promise<boolean> => {
    try {
      await mutation.mutateAsync(updatedConfig);
      return true; // Operation succeeded
    } catch (error) {
      return false; // Operation failed
    }
  };

  return {
    ...query,
    updateConfig,
    isUpdating: mutation.isPending,
  };
};
